import { useState, useRef } from "react";
import {
	Box,
	Paper,
	Zoom,
	Button,
	Typography,
	CircularProgress,
} from "@mui/material";
import { CloudUpload, CheckCircle, Error } from "@mui/icons-material";
import config from "../config/config";
import { LoadingButton } from "@mui/lab";
import api from "../Api/base";
import Api from "../Api/Api.json";
const UploadIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="17 8 12 3 7 8"></polyline>
		<line x1="12" y1="3" x2="12" y2="15"></line>
	</svg>
);

const UploadPrescription = () => {
	const [selectedFile, setSelectedFile] = useState(null);
	const [previewUrl, setPreviewUrl] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error', null
	const [patientId, setPatientId] = useState(0);
	const [base64String, setBase64String] = useState(null);
	const getUserID = async () => {
		if (!patientId) {
			const result = await api.post(Api.MUKTIBD_USER_API, {
				task: "getUserInfo",
			});
			if (result.success) {
				return result.data[0].id;
			} else {
				return 0;
			}
		}
		return patientId;
	};

	const fileInputRef = useRef(null);

	const handleFileSelect = (event) => {
		const file = event.target.files[0];

		if (file) {
			setSelectedFile(file);
			setUploadStatus(null);

			// Create preview URL
			const fileReader = new FileReader();
			fileReader.onload = () => {
				setPreviewUrl(fileReader.result);
				const base64String = fileReader.result.split(",")[1];
				setBase64String(base64String); // If you want to save it in state
			};
			fileReader.readAsDataURL(file);
		}
	};

	const handleDragOver = (event) => {
		event.preventDefault();
	};

	const handleDrop = (event) => {
		event.preventDefault();
		const file = event.dataTransfer.files[0];
		if (
			file &&
			(file.type === "image/jpeg" ||
				file.type === "image/png" ||
				file.type === "image/jpg")
		) {
			setSelectedFile(file);
			setUploadStatus(null);

			// Create preview URL
			const fileReader = new FileReader();
			fileReader.onload = () => {
				setPreviewUrl(fileReader.result);
				const base64String = fileReader.result.split(",")[1];
				setBase64String(base64String); // If you want to save it in state
			};
			fileReader.readAsDataURL(file);
		}
	};

	const handleUpload = async () => {
		if (!selectedFile) return;

		setUploading(true);

		try {
			// task:'savePrescriptionPhoto', patient_id:, photo: b64_string_of_photo
			// Simulate upload - replace with actual API call
			// await new Promise((resolve) => setTimeout(resolve, 2000));
			const id = await getUserID();
			const result = await api.post(Api.PRESCRIPTION_DATA_FETCH, {
				task: "savePrescriptionPhoto",
				patient_id: id,
				photo: base64String,
			});

			if (result.success) {
				setUploadStatus("success");
			} else {
				setUploadStatus("error");
			}
		} catch (error) {
			console.error("Upload failed:", error);
			setUploadStatus("error");
		} finally {
			setUploading(false);
		}
	};

	const resetUpload = () => {
		setSelectedFile(null);
		setPreviewUrl(null);
		setUploadStatus(null);
	};

	return (
		<Box
			className="max-w-2xl mx-auto px-5 py-3"
			style={{ minHeight: `calc(100vh - ${config.topbarHeight}px)` }}
		>
			<h1 className="text-3xl font-bold text-center mt-4 mb-6 top-0">
				ব্যাবস্থাপত্র আপলোড করুন
			</h1>
			<Zoom in={true}>
				<Paper elevation={3} className="flex flex-col w-full gap-3 p-6">
					{!selectedFile ? (
						<div
							className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
							onClick={() => fileInputRef.current.click()}
							onDragOver={handleDragOver}
							onDrop={handleDrop}
						>
							<input
								type="file"
								hidden
								accept="image/png, image/jpeg, image/jpg"
								onChange={handleFileSelect}
								ref={fileInputRef}
							/>
							<CloudUpload
								sx={{
									fontSize: 64,
									color: "primary.main",
									mb: 2,
								}}
							/>
							<Typography variant="h6" gutterBottom>
								আপনার ব্যাবস্থাপত্র আপলোড করুন
							</Typography>
							<Typography
								variant="body2"
								color="textSecondary"
								paragraph
							>
								ছবিটি টেনে এখানে ছাড়ুন অথবা ক্লিক করে ফাইল
								নির্বাচন করুন
							</Typography>
							<Typography variant="caption" color="textSecondary">
								সমর্থিত ফরম্যাট: JPG, JPEG, PNG
							</Typography>
						</div>
					) : (
						<div className="flex flex-col items-center">
							<div className="relative mb-4 w-full max-w-md">
								<img
									src={previewUrl}
									alt="Prescription preview"
									className="w-full h-auto rounded-lg border border-gray-200"
								/>
								<Button
									size="small"
									variant="contained"
									color="error"
									className="absolute top-2 right-2"
									onClick={resetUpload}
								>
									বাতিল
								</Button>
							</div>

							<div className="flex flex-col items-center w-full">
								<Typography variant="body1" gutterBottom>
									ফাইল: {selectedFile.name}
								</Typography>
								<Typography
									variant="body2"
									color="textSecondary"
									gutterBottom
								>
									সাইজ:{" "}
									{(selectedFile.size / 1024).toFixed(2)} KB
								</Typography>

								{uploadStatus === "success" ? (
									<div className="flex items-center text-green-600 mt-4 mb-2">
										<CheckCircle sx={{ mr: 1 }} />
										<Typography>
											আপলোড সফল হয়েছে!
										</Typography>
									</div>
								) : uploadStatus === "error" ? (
									<div className="flex items-center text-red-600 mt-4 mb-2">
										<Error sx={{ mr: 1 }} />
										<Typography>
											আপলোড ব্যর্থ হয়েছে, আবার চেষ্টা
											করুন
										</Typography>
									</div>
								) : (
									<LoadingButton
										variant="contained"
										color="primary"
										fullWidth
										size="large"
										disabled={uploading}
										onClick={handleUpload}
										sx={{ mt: 2 }}
										loading={uploading}
										startIcon={<UploadIcon />}
										loadingPosition="start"
									>
										{uploading
											? "আপলোড হচ্ছে..."
											: "আপলোড করুন"}
									</LoadingButton>
								)}

								{uploadStatus === "success" && (
									<Button
										variant="outlined"
										color="primary"
										fullWidth
										size="large"
										onClick={resetUpload}
										sx={{ mt: 2 }}
									>
										নতুন ব্যাবস্থাপত্র আপলোড করুন
									</Button>
								)}
							</div>
						</div>
					)}

					<div className="mt-6">
						<Typography variant="subtitle2" gutterBottom>
							গুরুত্বপূর্ণ নির্দেশনা:
						</Typography>
						<ul className="list-disc pl-5 text-sm text-gray-600">
							<li>
								ব্যাবস্থাপত্রের সব অংশ যেন পরিষ্কারভাবে দেখা
								যায়
							</li>
							<li>
								ব্যাবস্থাপত্রের তারিখ অবশ্যই দৃশ্যমান হতে হবে
							</li>
							<li>
								অনুগ্রহ করে নিশ্চিত করুন যে চিকিৎসকের নাম এবং
								স্বাক্ষর স্পষ্ট
							</li>
						</ul>
					</div>
				</Paper>
			</Zoom>
		</Box>
	);
};

export default UploadPrescription;
